import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * The board's modal: a scrim over the WHOLE screen — rail and pane included —
 * and the `.dmodal` card. Portalled into its own `.maie desk` island, because
 * the kit's `.dscrim` is `position:absolute` and would otherwise cover only the
 * content column. Escape and the scrim close it.
 */
export function TechModal({
  open,
  onClose,
  title,
  sub,
  children,
  foot,
  width = 520,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  sub?: ReactNode;
  children?: ReactNode;
  foot?: ReactNode;
  width?: number;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="maie desk kit-modal-dock" style={{ position: 'fixed', inset: 0, zIndex: 60 }}>
      <div className="dscrim" onClick={onClose} role="presentation" />
      <div
        className="dmodal dmodal-phone"
        style={{ width: `min(${width}px, calc(100vw - 32px))`, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto' }}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
      >
        <p className="t-title">{title}</p>
        {sub ? <p className="t-sec mut">{sub}</p> : null}
        {children}
        {foot ? <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>{foot}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
